import { useState, useEffect } from 'react';
import { useCookies } from 'react-cookie';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

function Onboarding({ api }) {
    const [cookies] = useCookies(['token']);
    const navigate = useNavigate();
    const { user, token, loading: authLoading } = useAuth();
    const [birthMonth, setBirthMonth] = useState(1);
    const [birthDay, setBirthDay] = useState(1);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [isPrivate, setIsPrivate] = useState(false);
    const [marketingOptIn, setMarketingOptIn] = useState(true);
    const [isNewUser, setIsNewUser] = useState(false);
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [username, setUsername] = useState('');
    const [editedUsername, setEditedUsername] = useState(false);

    // Use AuthContext user data instead of making a duplicate getMe call
    useEffect(() => {
        if (authLoading) return;

        if (!token) {
            navigate('/login');
            return;
        }

        if (user) {
            // AuthContext already handles redirecting completed users to /home
            // Prefill existing user data
            if (user.firstName) setFirstName(user.firstName);
            if (user.lastName) setLastName(user.lastName);
            if (user.username) setUsername(user.username);
            if (user.birthMonth) setBirthMonth(user.birthMonth);
            if (user.birthDay) setBirthDay(user.birthDay);
            if (user.isPrivate !== undefined) setIsPrivate(user.isPrivate);

            // Check if new user (missing name fields)
            if (!user.firstName || !user.lastName || !user.username) {
                setIsNewUser(true);
            } else {
                setIsNewUser(false);
            }
        }
    }, [user, token, authLoading, navigate]);

    const handleUsernameChange = (e) => {
        const input = e.target.value.replace(/[^a-zA-Z0-9.-]/g, "");
        setUsername(input);
        setEditedUsername(true);
    };

    useEffect(() => {
        if (!editedUsername && isNewUser) {
            const generatedUsername = `${firstName.trim().toLowerCase()}${firstName && lastName ? "." : ""}${lastName.trim().toLowerCase()}`;
            setUsername(generatedUsername || "");
        }
    }, [firstName, lastName, editedUsername, isNewUser]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            // Build update object with non-empty fields
            const updateData = {
                onboardingComplete: true,
                isPrivate: isPrivate  // Boolean field, always include
            };

            // Add non-empty string fields
            if (firstName && firstName.trim()) {
                updateData.firstName = firstName;
            }
            if (lastName && lastName.trim()) {
                updateData.lastName = lastName;
            }
            if (username && username.trim()) {
                updateData.username = username;
            }

            // Validate username if provided
            if (updateData.username && !/^[a-zA-Z0-9][a-zA-Z0-9.-]*[a-zA-Z0-9]$/.test(updateData.username)) {
                setError("Username must start and end with a letter or number and can only contain letters, numbers, hyphens, and periods.");
                setLoading(false);
                return;
            }

            // Validate required fields for new users
            if (isNewUser) {
                if (!updateData.firstName || !updateData.lastName || !updateData.username) {
                    setError("First name, last name, and username are required.");
                    setLoading(false);
                    return;
                }
            }

            // Step 1: Save birthday
            await api().updateBirthday(cookies.token, {
                birthDay: birthDay,
                birthMonth: birthMonth,
            });

            // Step 2: Save user data and preferences
            await api().updateMe(cookies.token, updateData);

            // Step 3: Save marketing preferences (all or none based on checkbox)
            await api().updateMarketingPrefs(cookies.token, {
                sendFollowerEmails: marketingOptIn,
                sendMonthlyEmails: marketingOptIn,
                sendFollowRecs: marketingOptIn
            });

            // All succeeded, navigate to home
            navigate('/home');

        } catch (err) {
            setError('Failed to save your preferences. Please try again.');
        }

        setLoading(false);
    };

    return (
        <div className="bg-gradient-to-b from-blue-400 to-green-700 min-h-screen flex items-center justify-center">
            <div className="bg-white p-8 rounded-lg shadow-lg max-w-sm w-full">
                <h2 className="text-2xl font-semibold mb-2 text-center">
                    Complete your profile
                </h2>
                <p className="text-gray-600 mb-6 text-center text-sm">
                    Just a few more things to get you started!
                </p>

                {error && <p className="text-red-500 text-center mb-4">{error}</p>}

                <form onSubmit={handleSubmit}>
                    {/* Name Section - Only for new users */}
                    {isNewUser && (
                        <div className="mb-6">
                            <h3 className="text-lg font-semibold mb-1">What's your name?</h3>
                            <p className="text-gray-600 text-sm mb-3">
                                This is how your friends will find you
                            </p>

                            <div className="flex gap-4 mb-4">
                                <div className="flex-1">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        First name
                                    </label>
                                    <input
                                        type="text"
                                        className="w-full p-2 border border-gray-300 rounded-md"
                                        placeholder="First name"
                                        value={firstName}
                                        onChange={(e) => setFirstName(e.target.value.replace(/[^a-zA-Z0-9\s]/g, ""))}
                                        required
                                    />
                                </div>

                                <div className="flex-1">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Last name
                                    </label>
                                    <input
                                        type="text"
                                        className="w-full p-2 border border-gray-300 rounded-md"
                                        placeholder="Last name"
                                        value={lastName}
                                        onChange={(e) => setLastName(e.target.value.replace(/[^a-zA-Z0-9\s]/g, ""))}
                                        required
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Username
                                </label>
                                <input
                                    type="text"
                                    className="w-full p-2 border border-gray-300 rounded-md"
                                    placeholder="Username"
                                    value={username}
                                    onChange={handleUsernameChange}
                                    onFocus={() => setEditedUsername(true)}
                                    required
                                />
                            </div>
                        </div>
                    )}

                    {/* Birthday Section */}
                    <div className="mb-6">
                        <h3 className="text-lg font-semibold mb-1">When's your birthday?</h3>
                        <p className="text-gray-600 text-sm mb-3">
                            We'll share this with your friends!
                        </p>

                        <div className="flex gap-4">
                            <div className="flex-1">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Month
                                </label>
                                <select
                                    value={birthMonth}
                                    onChange={(e) => setBirthMonth(Number(e.target.value))}
                                    className="w-full p-2 border border-gray-300 rounded-md"
                                >
                                    {Array.from({ length: 12 }, (_, i) => (
                                        <option key={i + 1} value={i + 1}>
                                            {new Date(2000, i, 1).toLocaleString('default', { month: 'long' })}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="flex-1">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Day
                                </label>
                                <select
                                    value={birthDay}
                                    onChange={(e) => setBirthDay(Number(e.target.value))}
                                    className="w-full p-2 border border-gray-300 rounded-md"
                                >
                                    {Array.from({ length: 31 }, (_, i) => (
                                        <option key={i + 1} value={i + 1}>
                                            {i + 1}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>

                    {/* Privacy Section */}
                    <div className="mb-6">
                        <h3 className="text-lg font-semibold mb-3">Privacy</h3>

                        <div className="flex items-start space-x-3 p-3 bg-gray-50 rounded-md">
                            <input
                                type="checkbox"
                                id="privateProfile"
                                className="h-4 w-4 mt-1 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                checked={isPrivate}
                                onChange={(e) => setIsPrivate(e.target.checked)}
                            />
                            <div>
                                <label htmlFor="privateProfile" className="text-sm font-medium text-gray-900 cursor-pointer">
                                    Make my profile private
                                </label>
                                <p className="text-xs text-gray-600 mt-1">
                                    Private accounts require approval for others to follow you and see your birthday
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Email Preferences Section */}
                    <div className="mb-6">
                        <h3 className="text-lg font-semibold mb-3">Stay connected</h3>

                        <div className="flex items-start space-x-3 p-3 bg-gray-50 rounded-md">
                            <input
                                type="checkbox"
                                id="marketingEmails"
                                className="h-4 w-4 mt-1 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                checked={marketingOptIn}
                                onChange={(e) => setMarketingOptIn(e.target.checked)}
                            />
                            <div>
                                <label htmlFor="marketingEmails" className="text-sm font-medium text-gray-900 cursor-pointer">
                                    Send me email notifications and updates
                                </label>
                                <p className="text-xs text-gray-600 mt-1">
                                    Get monthly birthday reminders, follower notifications, and friend recommendations
                                </p>
                            </div>
                        </div>
                    </div>

                    <button
                        type="submit"
                        disabled={loading}
                        className={`w-full p-2 text-white rounded-md ${
                            loading
                                ? 'bg-gray-400 cursor-not-allowed'
                                : 'bg-blue-600 hover:bg-blue-700'
                        }`}
                    >
                        {loading ? 'Saving...' : 'Complete setup'}
                    </button>
                </form>
            </div>
        </div>
    );
}

export default Onboarding;
